import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { fetchCadastros, createCadastro, updateCadastro, deleteCadastro } from '@/services/cadastroService';

export const useGenericCadastro = (tableName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCadastros(tableName);
      setData(result);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${tableName}:`, err);
      setError(err.message);
      toast({
        title: 'Erro',
        description: `Falha ao carregar dados de ${tableName}.`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [tableName, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const add = async (payload) => {
    setLoading(true);
    try {
      const newItem = await createCadastro(tableName, payload);
      setData([newItem, ...data]);
      toast({
        title: 'Sucesso',
        description: 'Registro criado com sucesso.',
      });
      return newItem;
    } catch (err) {
      console.error(`Error creating ${tableName}:`, err);
      toast({
        title: 'Erro',
        description: err.message || 'Falha ao criar registro.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const update = async (id, payload) => {
    setLoading(true);
    try {
      const updatedItem = await updateCadastro(tableName, id, payload);
      setData(data.map(item => (item.id === id ? updatedItem : item)));
      toast({
        title: 'Sucesso',
        description: 'Registro atualizado com sucesso.',
      });
      return updatedItem;
    } catch (err) {
      console.error(`Error updating ${tableName}:`, err);
      toast({
        title: 'Erro',
        description: err.message || 'Falha ao atualizar registro.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    setLoading(true);
    try {
      await deleteCadastro(tableName, id);
      setData(data.filter(item => item.id !== id));
      toast({
        title: 'Sucesso',
        description: 'Registro removido com sucesso.',
      });
    } catch (err) {
      console.error(`Error deleting ${tableName}:`, err);
      toast({
        title: 'Erro',
        description: err.message || 'Falha ao remover registro.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    loadData,
    add,
    update,
    remove,
  };
};